import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HygieneActivityReport, HygieneLocationFilters, HygieneStaftReportService } from '../hygiene-staft-report.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import flatpickr from 'flatpickr';
import { HygineCleaningDialogComponent } from '../hygine-cleaning-dialog/hygine-cleaning-dialog.component';
import { HygineReportService } from '../../Hygiene Report/hygine-report.service';
export interface HygieneActivityDisplayItem {
  floorName: string;
  roomDisplayName: string;
  hygieneId: number;
  activityId: number;
  activityName: string;
  imagePath: string;
  imageUrl: string;
  capturedTime?: string;
  slot?: string;
  latitude?: number | string;
  longitude?: number | string;
  campusName?: string;
  buildingName?: string;
  employeeName?: string;
  designation?: string;
  mobileNo?: string;
}
@Component({
  selector: 'app-hygine-cleaning',
  templateUrl: './hygine-cleaning.component.html',
  styleUrls: ['./hygine-cleaning.component.scss']
})
export class HygineCleaningComponent {
   /** Same hygiene dropdown + activityReport filters as Hygiene Staff Attendance */
  schoolOrCollege: 'COLLEGE' | 'SCHOOL' = 'COLLEGE';
  boardingSegment: 'RESIDENTIAL' | 'DAY_SCHOLAR' = 'RESIDENTIAL';

  activeDateTab: 'today' | 'yesterday' | 'last7' | 'month' | 'custom' = 'today';
  startDate = '';
  endDate = '';
  dateRangePickerInit = false;
  customStartDate = '';
  customEndDate = '';

  stateList: any[] = [];
  filteredStateList: any[] = [];
  selectedStateId: any = null;
  selectedStateName = '';
  stateQuery = '';

  cityList: any[] = [];
  filteredCityList: any[] = [];
  selectedCityId: any = null;
  selectedCityName = '';
  cityQuery = '';

  campusList: any[] = [];
  filteredCampusList: any[] = [];
  selectedCampusId: any = null;
  selectedCampusName = '';
  campusQuery = '';

  buildingList: any[] = [];
  filteredBuildingList: any[] = [];
  selectedBuildingId: any = null;
  selectedBuildingName = '';
  buildingQuery = '';

  blockList: { id: number; name: string }[] = [];
  selectedBlockId: number | null = null;
  selectedBlockName = '';

  stateOpen = false;
  cityOpen = false;
  campusOpen = false;
  buildingOpen = false;
  blockOpen = false;

  activityReportData: HygieneActivityReport | null = null;
  imageErrorIds = new Set<number>();
  private s3Base = (environment as any).s3imageUrl || '';
   mapIconPath = 'assets/image/college-map-icon.svg';

  private getDropdownFilters(): HygieneLocationFilters {
    return {
      schooolOrCollege: this.schoolOrCollege,
      boardingType: this.boardingSegment
    };
  }

  get stateTriggerText(): string {
    if (this.selectedStateId != null && this.selectedStateName) return this.selectedStateName;
    return 'Select State';
  }

  /** Floor/room from API (floorName, roomDisplayName); when no data, null so UI shows empty until report loads */
  selectedFloor: string | null = null;
  selectedRoom: string | null = null;

  pageSize = 10;
  currentPage = 1;

  /** Floors from API response (building-based); empty until report loaded */
  get floors(): string[] {
    const data = this.activityReportData;
    if (!data?.floors?.length) return [];
    const names = data.floors.map(f => String(f.floorName)).filter(n => n);
    return [...new Set(names)];
  }

  /** Rooms for selected floor from API response */
  get rooms(): string[] {
    const data = this.activityReportData;
    const floorKey = this.selectedFloor;
    if (!data?.floors?.length || floorKey == null) return [];
    const floor = data.floors.find(f => String(f.floorName) === floorKey);
    if (!floor?.rooms?.length) return [];
    return floor.rooms.map(r => String((r as any).roomDisplayName ?? r.roomNo));
  }

  /** Flatten floors/rooms/images and filter by selected floor/room */
  get flattenedPictures(): HygieneActivityDisplayItem[] {
    const data = this.activityReportData;
    if (!data?.floors?.length) return [];
    const items: HygieneActivityDisplayItem[] = [];
    for (const floor of data.floors) {
      const defaultFloorName = String(floor.floorName ?? '');
      for (const room of floor.rooms || []) {
        const defaultRoomDisplayName = String((room as any).roomDisplayName ?? room.roomNo ?? '');
        for (const img of room.images || []) {
          const api = img as any;
          const fn = api.fileName?.trim?.() || api.fileName || '';
          const iu = api.imageUrl?.trim?.() || api.imageUrl || '';
          const ip = api.imagePath?.trim?.() || api.imagePath || '';
          const raw = fn || iu || ip;
          const imageUrl = (raw && (raw.startsWith('http://') || raw.startsWith('https://')))
            ? raw
            : (this.s3Base && raw ? `${this.s3Base.replace(/\/$/, '')}/${String(raw).replace(/^\//, '')}` : raw);
          const apiImg = img as any;
          const staff0 = api.staffDetails?.[0] ?? apiImg?.staffDetails?.[0];
          const employeeName = api.employeeName || (staff0 && (staff0.employeeName || staff0.staffName)) || undefined;
          const floorName = (api.floorName != null && String(api.floorName).trim() !== '') ? String(api.floorName) : defaultFloorName;
          const roomDisplayName = (api.roomDisplayName != null && String(api.roomDisplayName).trim() !== '') ? String(api.roomDisplayName) : defaultRoomDisplayName;
          items.push({
            floorName,
            roomDisplayName,
            hygieneId: img.hygieneId ?? api.hygieneId,
            activityId: img.activityId ?? api.activityId,
            activityName: api.activityName || img.activityName || '',
            imagePath: api.imagePath || api.fileName || '',
            imageUrl,
            /** Prefer hygieneDate (ISO) so card shows DD-MM-YYYY · time; capturedTime alone is often time-only */
            capturedTime: api.hygieneDate || api.capturedTime || undefined,
            slot: api.slot || undefined,
            latitude: api.latitude ?? img.latitude,
            longitude: api.longitude ?? img.longitude,
            campusName: api.campusName ?? this.selectedCampusName ?? undefined,
            buildingName: api.buildingName ?? this.selectedBuildingName ?? undefined,
            employeeName: employeeName || undefined,
            designation: api.designation || undefined,
            mobileNo: api.mobileNo != null && api.mobileNo !== '' ? String(api.mobileNo) : undefined
          });
        }
      }
    }
    const floorKey = this.selectedFloor;
    const roomKey = this.selectedRoom;
    if (floorKey == null) return items;
    const byFloor = items.filter(p => p.floorName === floorKey);
    if (roomKey != null && roomKey !== '') {
      return byFloor.filter(p => p.roomDisplayName === roomKey);
    }
    return byFloor;
  }

  get totalPictures(): number {
    const d: any = this.activityReportData as any;
    const totalRaw = d?.totalRecords ?? d?.totalCount ?? d?.totalElements ?? d?.total;
    const n = totalRaw != null && totalRaw !== '' ? Number(totalRaw) : NaN;
    if (Number.isFinite(n) && n >= 0) return n;
    return this.flattenedPictures.length;
  }
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalPictures / this.pageSize));
  }
  get paginatedPictures(): HygieneActivityDisplayItem[] {
    // With paged API, backend already returns one slice per request.
    return this.flattenedPictures;
  }
  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  /** Pagination: first 10 pages, dots, window around current, dots, last 1–2 pages; back/forward 10 */
  get paginationSlots(): (number | 'ellipsis')[] {
    const total = this.totalPages;
    const cur = this.currentPage;
    if (total <= 14) return Array.from({ length: total }, (_, i) => i + 1);
    const first10 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const last2 = [total - 1, total];
    if (cur <= 10) return [...first10, 'ellipsis', ...last2];
    if (cur >= total - 1) return [...first10, 'ellipsis', ...last2];
    const winStart = Math.max(11, cur - 2);
    const winEnd = Math.min(total - 2, cur + 2);
    const windowPages = Array.from({ length: winEnd - winStart + 1 }, (_, i) => winStart + i);
    return [...first10, 'ellipsis', ...windowPages, 'ellipsis', ...last2];
  }

  goBack10(): void {
    this.setPage(Math.max(1, this.currentPage - 10));
  }

  goForward10(): void {
    this.setPage(Math.min(this.totalPages, this.currentPage + 10));
  }

  /** For template: summary counts from activity report */
  get reportData(): { todayPhotoCount: number; yesterdayPhotoCount: number; last7DaysPhotoCount: number; thisMonthPhotoCount: number } | null {
    const d = this.activityReportData;
    if (!d) return null;
    return {
      todayPhotoCount: d.todayPhotoCount ?? 0,
      yesterdayPhotoCount: d.yesterdayPhotoCount ?? 0,
      last7DaysPhotoCount: d.last7DaysPhotoCount ?? 0,
      thisMonthPhotoCount: d.thisMonthPhotoCount ?? 0
    };
  }

  @ViewChild('dateRangeInput') dateRangeInput!: ElementRef;

  constructor(
    private hygieneService: HygieneStaftReportService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: Event): void {
    const target = e.target as HTMLElement;
    const isTrigger = target.closest('.hr-combobox-trigger');
    const isPanel = target.closest('.hr-combobox-panel');
    if (!isTrigger && !isPanel) {
      this.closeAllDropdowns();
    }
  }

  ngOnInit(): void {
    this.activeDateTab = 'today';
    this.setDateRangeForTab();
    this.loadStates();
    this.loadCities();
    this.fetchReport();
  }

  selectSchoolOrCollege(mode: 'COLLEGE' | 'SCHOOL'): void {
    if (this.schoolOrCollege === mode) return;
    this.schoolOrCollege = mode;
    this.onOrgFiltersChanged();
  }

  selectBoarding(mode: 'RESIDENTIAL' | 'DAY_SCHOLAR'): void {
    if (this.boardingSegment === mode) return;
    this.boardingSegment = mode;
    this.onOrgFiltersChanged();
  }

  /** Clear city → block when college/boarding changes */
  private clearLocationBelowState(): void {
    this.selectedCityId = null;
    this.selectedCityName = '';
    this.cityQuery = '';
    this.cityList = [];
    this.filteredCityList = [];
    this.selectedCampusId = null;
    this.selectedCampusName = '';
    this.campusQuery = '';
    this.campusList = [];
    this.filteredCampusList = [];
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.buildingQuery = '';
    this.buildingList = [];
    this.filteredBuildingList = [];
    this.selectedBlockId = null;
    this.selectedBlockName = '';
    this.blockList = [];
  }

  private onOrgFiltersChanged(): void {
    this.clearLocationBelowState();
    this.loadStates();
    this.loadCities();
    this.fetchReport();
  }

  loadStates(): void {
    this.hygieneService.getStates(this.getDropdownFilters()).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.stateList ?? res?.states ?? res?.data ?? []);
        this.stateList = list || [];
        this.filteredStateList = [...this.stateList];
      },
      error: () => {
        this.stateList = [];
        this.filteredStateList = [];
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.activeDateTab === 'custom' && this.dateRangeInput?.nativeElement && !this.dateRangePickerInit) {
      this.dateRangePickerInit = true;
      setTimeout(() => this.initDateRangePicker(), 100);
    }
  }

  /** Custom tab: any past first click; second click within ±5 days of that day (sorted range), capped at today. */
  private readonly customRangeMaxSpanDays = 5;

  private startOfLocalDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private addLocalDays(base: Date, delta: number): Date {
    const x = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    x.setDate(x.getDate() + delta);
    return x;
  }

  private localDateToYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private getTodayLocal(): Date {
    return this.startOfLocalDay(new Date());
  }

  initDateRangePicker(): void {
    const el = this.dateRangeInput?.nativeElement;
    if (!el) return;
    const today0 = this.getTodayLocal();
    flatpickr(el, {
      mode: 'range',
      dateFormat: 'Y-m-d',
      maxDate: today0,
      defaultDate:
        this.customStartDate && this.customEndDate
          ? [this.customStartDate, this.customEndDate]
          : undefined,
      onChange: (dates: Date[], _dateStr: string, instance: any) => {
        const t0 = this.getTodayLocal();
        if (dates.length >= 2) {
          this.customStartDate = this.localDateToYmd(dates[0]);
          this.customEndDate = this.localDateToYmd(dates[1]);
          this.startDate = this.customStartDate;
          this.endDate = this.customEndDate;
          this.fetchReport();
          instance.set('minDate', null);
          instance.set('maxDate', t0);
        } else if (dates.length === 1) {
          const anchor = this.startOfLocalDay(dates[0]);
          const minSecond = this.addLocalDays(anchor, -this.customRangeMaxSpanDays);
          const maxForward = this.addLocalDays(anchor, this.customRangeMaxSpanDays);
          const maxSecond = maxForward.getTime() > t0.getTime() ? t0 : maxForward;
          instance.set('minDate', minSecond);
          instance.set('maxDate', maxSecond);
          this.customStartDate = this.localDateToYmd(dates[0]);
          this.customEndDate = '';
          this.startDate = this.customStartDate;
          this.endDate = '';
        } else {
          this.customStartDate = '';
          this.customEndDate = '';
          instance.set('minDate', null);
          instance.set('maxDate', t0);
        }
      }
    });
  }

  setDateRangeForTab(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    switch (this.activeDateTab) {
      case 'today':
        this.startDate = fmt(today);
        this.endDate = fmt(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        this.startDate = fmt(yesterday);
        this.endDate = fmt(yesterday);
        break;
      case 'last7':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 6);
        this.startDate = fmt(weekAgo);
        this.endDate = fmt(today);
        break;
      case 'month':
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        this.startDate = fmt(first);
        this.endDate = fmt(today);
        break;
      case 'custom':
        if (this.customStartDate && this.customEndDate) {
          this.startDate = this.customStartDate;
          this.endDate = this.customEndDate;
        } else {
          this.startDate = '';
          this.endDate = '';
        }
        break;
      default:
        this.startDate = '';
        this.endDate = '';
    }
  }

  selectDateTab(tab: 'today' | 'yesterday' | 'last7' | 'month' | 'custom'): void {
    this.activeDateTab = tab;
    if (tab !== 'custom') {
      this.dateRangePickerInit = false;
      this.setDateRangeForTab();
      this.fetchReport();
    } else {
      this.setDateRangeForTab();
      if (this.customStartDate && this.customEndDate) {
        this.fetchReport();
      } else {
        this.activityReportData = null;
      }
    }
  }

  /** When `resetPaging` is true (default), used from the "All Floors" tab — go to page 1. After a paged API response, pass false so the active page is not overwritten. */
  selectAllFloors(resetPaging = true): void {
    this.selectedFloor = null;
    this.selectedRoom = null;
    if (resetPaging) this.currentPage = 1;
  }

  selectFloor(floor: string): void {
    this.selectedFloor = floor;
    const data = this.activityReportData;
    const fl = data?.floors?.find(f => String(f.floorName) === floor);
    this.selectedRoom = fl?.rooms?.[0] != null ? String((fl.rooms[0] as any).roomDisplayName ?? fl.rooms[0].roomNo) : null;
    this.currentPage = 1;
  }

  selectRoom(room: string): void {
    this.selectedRoom = room;
    this.currentPage = 1;
  }

  /** No state → empCities; state selected → empStateCities */
  loadCities(): void {
    const f = this.getDropdownFilters();
    const applyList = (res: any) => {
      const list = Array.isArray(res) ? res : (res?.cityList ?? res?.cities ?? res?.data ?? []);
      this.cityList = list || [];
      this.filteredCityList = [...this.cityList];
    };
    if (this.selectedStateId != null) {
      this.hygieneService.getCities(this.selectedStateId, f).subscribe({
        next: applyList,
        error: () => {
          this.cityList = [];
          this.filteredCityList = [];
        }
      });
    } else {
      // this.hygieneService.getEmpCities(f).subscribe({
      //   next: applyList,
      //   error: () => {
      //     this.cityList = [];
      //     this.filteredCityList = [];
      //   }
      // });
    }
  }

  closeAllDropdowns(): void {
    this.stateOpen = false;
    this.cityOpen = false;
    this.campusOpen = false;
    this.buildingOpen = false;
    this.blockOpen = false;
  }

  openState(): void {
    this.closeAllDropdowns();
    this.stateOpen = true;
    this.stateQuery = '';
    this.filterStates();
  }

  filterStates(): void {
    const q = (this.stateQuery || '').toLowerCase();
    this.filteredStateList = this.stateList.filter((s: any) =>
      (s.stateName ?? s.name ?? '').toString().toLowerCase().includes(q)
    );
  }

  selectState(s: any): void {
    this.selectedStateId = s?.stateId ?? s?.id ?? null;
    this.selectedStateName = s ? (s.stateName ?? s.name ?? '') : '';
    this.stateOpen = false;
    this.stateQuery = '';
    this.selectedCityId = null;
    this.selectedCityName = '';
    this.cityQuery = '';
    this.selectedCampusId = null;
    this.selectedCampusName = '';
    this.campusList = [];
    this.filteredCampusList = [];
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.buildingList = [];
    this.filteredBuildingList = [];
    this.selectedBlockId = null;
    this.selectedBlockName = '';
    this.blockList = [];
    this.loadCities();
  }

  selectAllStates(): void {
    this.selectedStateId = null;
    this.selectedStateName = '';
    this.stateOpen = false;
    this.stateQuery = '';
    this.filteredStateList = [...this.stateList];
    this.selectedCityId = null;
    this.selectedCityName = '';
    this.cityQuery = '';
    this.selectedCampusId = null;
    this.selectedCampusName = '';
    this.campusList = [];
    this.filteredCampusList = [];
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.buildingList = [];
    this.filteredBuildingList = [];
    this.selectedBlockId = null;
    this.selectedBlockName = '';
    this.blockList = [];
    this.loadCities();
  }

  openCity(): void {
    this.closeAllDropdowns();
    this.cityOpen = true;
    this.cityQuery = '';
    this.filterCities();
  }

  openCampus(): void {
    this.closeAllDropdowns();
    this.campusOpen = true;
    this.campusQuery = '';
    this.filterCampuses();
  }

  openBuilding(): void {
    this.closeAllDropdowns();
    this.buildingOpen = true;
    this.buildingQuery = '';
    this.filterBuildings();
  }

  openBlock(): void {
    this.closeAllDropdowns();
    this.blockOpen = true;
  }

  filterCities(): void {
    const q = this.cityQuery.toLowerCase();
    this.filteredCityList = this.cityList.filter((c: any) =>
      (c.cityName ?? c.name ?? '').toString().toLowerCase().includes(q)
    );
  }
  filterCampuses(): void {
    const q = this.campusQuery.toLowerCase();
    this.filteredCampusList = this.campusList.filter((c: any) =>
      (c.campusName ?? c.name ?? '').toString().toLowerCase().includes(q)
    );
  }
  filterBuildings(): void {
    const q = this.buildingQuery.toLowerCase();
    this.filteredBuildingList = this.buildingList.filter((b: any) =>
      (b.buildingName ?? b.name ?? '').toString().toLowerCase().includes(q)
    );
  }

  selectCity(c: any): void {
    this.selectedCityId = c?.cityId ?? c?.id ?? null;
    this.selectedCityName = c ? (c.cityName ?? c.name ?? '') : '';
    this.cityOpen = false;
    this.selectedCampusId = null;
    this.selectedCampusName = '';
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.selectedBlockId = null;
    this.selectedBlockName = '';
    this.blockList = [];
    this.campusList = [];
    this.buildingList = [];
    if (c) {
      const id = c.cityId ?? c.id;
      this.hygieneService.getCampuses(id, this.getDropdownFilters()).subscribe({
        next: (res: any) => {
          this.campusList = Array.isArray(res) ? res : (res || []);
          this.filteredCampusList = [...this.campusList];
        },
        error: () => { this.campusList = []; this.filteredCampusList = []; }
      });
    }
  }

  selectCampus(c: any): void {
    this.selectedCampusId = c?.campusId ?? c?.id ?? null;
    this.selectedCampusName = c ? (c.campusName ?? c.name ?? '') : '';
    this.campusOpen = false;
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.selectedBlockId = null;
    this.selectedBlockName = '';
    this.blockList = [];
    this.buildingList = [];
    if (c) {
      const id = c.campusId ?? c.id;
      this.hygieneService.getBuildings(id, this.getDropdownFilters()).subscribe({
        next: (res: any) => {
          this.buildingList = Array.isArray(res) ? res : (res || []);
          this.filteredBuildingList = [...this.buildingList];
        },
        error: () => { this.buildingList = []; this.filteredBuildingList = []; }
      });
    }
  }

  selectBuilding(b: any): void {
    this.selectedBuildingId = b?.buildingId ?? b?.id ?? null;
    this.selectedBuildingName = b ? (b.buildingName ?? b.name ?? '') : '';
    this.buildingOpen = false;
    this.selectedBlockId = null;
    this.selectedBlockName = '';
    this.blockList = [];
    if (this.selectedBuildingId != null) {
      this.hygieneService.getBuildingBlocks(this.selectedBuildingId, this.getDropdownFilters()).subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : (res?.blockList ?? res?.blocks ?? res?.data ?? []);
          this.blockList = (list || []).map((x: any) => ({
            id: x.blockId ?? x.id ?? x.block_id,
            name: x.blockName ?? x.name ?? x.block_name ?? String(x)
          })).filter((x: { id: number; name: string }) => x.id != null && x.name != null);
          // Single block: use it for activity report automatically
          if (this.blockList.length === 1) {
            const only = this.blockList[0];
            this.selectedBlockId = only.id;
            this.selectedBlockName = only.name;
            if (this.selectedCityId && this.selectedCampusId) {
              this.fetchReport();
            }
          }
        },
        error: () => { this.blockList = []; }
      });
    }
  }

  selectBlock(b: { id: number; name: string }): void {
    this.selectedBlockId = b.id;
    this.selectedBlockName = b.name;
    this.blockOpen = false;
    // Reload activity report with blockId for Hygiene Cleaning
    if (this.selectedBuildingId != null && this.selectedCityId && this.selectedCampusId) {
      this.fetchReport();
    }
  }

  onSearch(): void {
    this.fetchReport();
  }

  fetchReport(resetPage: boolean = true): void {
    if (resetPage) this.currentPage = 1;
    this.setDateRangeForTab();
    if (this.activeDateTab === 'custom' && (!this.startDate || !this.endDate)) {
      this.activityReportData = null;
      return;
    }
    const filterTypeMap: Record<string, string> = { today: 'TODAY', yesterday: 'YESTERDAY', last7: 'LAST7DAYS', month: 'THISMONTH' };
    const params: any = {};
    if (this.selectedStateId != null) params['stateId'] = String(this.selectedStateId);
    if (this.selectedCityId) params['cityId'] = String(this.selectedCityId);
    if (this.selectedCampusId) params['campusId'] = String(this.selectedCampusId);
    if (this.selectedBuildingId) params['buildingId'] = String(this.selectedBuildingId);
    if (this.selectedBlockId != null) params['blockId'] = String(this.selectedBlockId);
    const loc = this.getDropdownFilters();
    params['schooolOrCollege'] = loc.schooolOrCollege!;
    params['boardingType'] = loc.boardingType!;
    if (this.activeDateTab === 'custom') {
      params['startDate'] = this.startDate;
      params['endDate'] = this.endDate;
    } else {
      params['filterType'] = filterTypeMap[this.activeDateTab] ?? 'TODAY';
    }
    params['page'] = this.currentPage;
    params['pageSize'] = this.pageSize;
    this.imageErrorIds.clear();
    this.spinner.show();
    this.hygieneService.getActivityReport(params).subscribe({
      next: (res: any) => {
        this.activityReportData = this.mapActivityReportResponse(res);
        this.applyFirstFloorAndRoomFromResponse();
        this.spinner.hide();
      },
      error: () => {
        this.activityReportData = null;
        this.selectedFloor = null;
        this.selectedRoom = null;
        this.spinner.hide();
      }
    });
  }

  /** After report load: show all images as cards (Today/Yesterday etc.) – one card per image from floors[].rooms[].images[] */
  private applyFirstFloorAndRoomFromResponse(): void {
    this.selectAllFloors(false);
  }

  mapActivityReportResponse(res: any): HygieneActivityReport {
    const data = res?.data ?? res;
    let floors = Array.isArray(data?.floors) ? data.floors : [];
    if (floors.length === 0 && Array.isArray(data?.images) && data.images.length > 0) {
      floors = [{ floorName: '', rooms: [{ roomNo: '', roomDisplayName: '', images: data.images }] }];
    }
    const totalRaw =
      data?.totalRecords ??
      data?.totalCount ??
      data?.totalElements ??
      data?.total ??
      res?.totalRecords ??
      res?.totalCount ??
      res?.totalElements ??
      res?.total;
    const n = totalRaw != null && totalRaw !== '' ? Number(totalRaw) : NaN;
    /** Flag may live on `data` or on the envelope next to `data` */
    const apiBuildingFlag = data?.isBuilding ?? res?.isBuilding;
    const hasFloorRoomStructure = floors.some((fl: any) => {
      const fn = String(fl?.floorName ?? '').trim();
      if (fn.length > 0) return true;
      const rooms = Array.isArray(fl?.rooms) ? fl.rooms : [];
      return rooms.some((r: any) => {
        const rd = String(r?.roomDisplayName ?? r?.roomNo ?? '').trim();
        return rd.length > 0;
      });
    });
    const explicitIsBuilding =
      apiBuildingFlag === true ||
      apiBuildingFlag === 1 ||
      (typeof apiBuildingFlag === 'string' &&
        ['true', '1', 'yes'].includes(apiBuildingFlag.trim().toLowerCase()));
    /** Respect API `isBuilding: true`; if omitted/null, infer from floors/rooms */
    const isBuilding =
      explicitIsBuilding ||
      ((apiBuildingFlag === null || apiBuildingFlag === undefined) && hasFloorRoomStructure);
    const springNumber =
      data?.number ??
      res?.number ??
      data?.pageable?.pageNumber ??
      res?.pageable?.pageNumber;
    const rawCurrentPage = data?.currentPage ?? res?.currentPage;
    let currentPage: number | undefined;
    if (springNumber != null && springNumber !== '' && Number.isFinite(Number(springNumber))) {
      currentPage = Math.max(1, Math.floor(Number(springNumber)) + 1);
    } else if (rawCurrentPage != null && rawCurrentPage !== '' && Number.isFinite(Number(rawCurrentPage))) {
      currentPage = Math.max(1, Math.floor(Number(rawCurrentPage)) + 1);
    }
    return {
      todayPhotoCount: data?.todayPhotoCount ?? 0,
      yesterdayPhotoCount: data?.yesterdayPhotoCount ?? 0,
      last7DaysPhotoCount: data?.last7DaysPhotoCount ?? 0,
      thisMonthPhotoCount: data?.thisMonthPhotoCount ?? 0,
      isBuilding,
      floors,
      totalRecords: Number.isFinite(n) && n >= 0 ? n : undefined,
      totalPages: data?.totalPages ?? res?.totalPages,
      currentPage
    };
  }

  onImageError(hygieneId: number): void {
    this.imageErrorIds.add(hygieneId);
  }

  /** Pass full picture object (floor, room, geolocation, campus, building) to dialog */
  openImageDialog(p: HygieneActivityDisplayItem): void {
    this.dialog.open(HygineCleaningDialogComponent, {
      data: { picture: p },
      width: '95vw',
      maxWidth: '720px',
      minWidth: '320px',
      panelClass: 'hygiene-cleaning-dialog'
    });
  }

  openMap(lat: string | number, lng: string | number): void {
    if (lat == null || lng == null) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(lat))},${encodeURIComponent(String(lng))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Map icon click: stop card click and open map (uses lat/lng when present) */
  onMapIconClick(event: Event, lat: string | number | null | undefined, lng: string | number | null | undefined): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (lat != null && lng != null && lat !== '' && lng !== '') {
      this.openMap(lat, lng);
    } else {
      window.open('https://www.google.com/maps', '_blank', 'noopener,noreferrer');
    }
  }

  /** Card second row: date + time (same rules as Hygiene / Staff Attendance cards). */
  getCleaningCardDateTime(p: HygieneActivityDisplayItem): string {
    const raw = (p.capturedTime ?? (p as any).hygieneDate ?? (p as any).uploadedTime ?? '') as string;
    return this.formatHygieneDateTimeForCard(raw);
  }

  formatHygieneDateTimeForCard(dateStr: string): string {
    if (!dateStr) return '';
    const s = String(dateStr).trim();

    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
      const dt = new Date(s);
      if (!Number.isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const mo = dt.getMonth() + 1;
        const d = dt.getDate();
        const hh = dt.getHours();
        const mi = dt.getMinutes();
        const sec = dt.getSeconds();
        const dd = String(d).padStart(2, '0');
        const mm = String(mo).padStart(2, '0');
        const dateLabel = `${dd}-${mm}-${y}`;
        const isMidnight = hh === 0 && mi === 0 && sec === 0;
        if (isMidnight) {
          return dateLabel;
        }
        const ampm = hh >= 12 ? 'PM' : 'AM';
        const h12 = hh % 12 || 12;
        const timeLabel = `${h12}:${String(mi).padStart(2, '0')}:${String(sec).padStart(2, '0')} ${ampm}`;
        return `${dateLabel} · ${timeLabel}`;
      }
    }

    let y = 0;
    let mo = 0;
    let d = 0;
    let hh = 0;
    let mi = 0;
    let sec = 0;
    let parsed = false;

    if (/^\d{2}-\d{2}-\d{4}/.test(s)) {
      const [datePart, timePart] = s.split(/\s+/);
      const [ds, ms, ys] = datePart.split('-').map((x) => parseInt(x, 10));
      if (![ds, ms, ys].some((n) => Number.isNaN(n))) {
        d = ds;
        mo = ms;
        y = ys;
        if (timePart) {
          const tp = timePart.split(/[:.]/).slice(0, 3).map((x) => parseInt(x, 10));
          hh = tp[0] || 0;
          mi = tp[1] || 0;
          sec = tp[2] || 0;
        }
        parsed = true;
      }
    }

    if (!parsed) {
      return s;
    }

    const dd = String(d).padStart(2, '0');
    const mm = String(mo).padStart(2, '0');
    const dateLabel = `${dd}-${mm}-${y}`;
    const isMidnight = hh === 0 && mi === 0 && sec === 0;
    if (isMidnight) {
      return dateLabel;
    }
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    const timeLabel = `${h12}:${String(mi).padStart(2, '0')}:${String(sec).padStart(2, '0')} ${ampm}`;
    return `${dateLabel} · ${timeLabel}`;
  }

  setPage(page: number): void {
    const p = Math.max(1, Math.min(page, this.totalPages));
    if (p === this.currentPage) return;
    this.currentPage = p;
    this.fetchReport(false);
  }

  /** Ordinal suffix for numeric floor: 1st, 2nd, 3rd, etc. Returns empty for non-numeric floor names */
  getFloorOrdinal(floorName: string): string {
    if (floorName == null || floorName === '') return '';
    const n = Number(floorName);
    if (Number.isNaN(n)) return ' ';
    const mod100 = n % 100;
    const mod10 = n % 10;
    if (mod100 >= 11 && mod100 <= 13) return 'th ';
    if (mod10 === 1) return 'st ';
    if (mod10 === 2) return 'nd ';
    if (mod10 === 3) return 'rd ';
    return 'th ';
  }

  /** Card meta line: "floorName floor : Room no. roomDisplayName" */
  getFloorRoomLine(floorName: string, roomDisplayName: string): string {
    if (floorName == null || roomDisplayName == null) return '';
    return `${floorName}${this.getFloorOrdinal(floorName)}floor : Room no. ${roomDisplayName}`;
  }

  /** Label when no building selected: "Today's Pictures", "Yesterday's Pictures", etc. */
  getDateTabPicturesLabel(): string {
    const labels: Record<string, string> = {
      today: "Today's Pictures",
      yesterday: "Yesterday's Pictures",
      last7: "Last 7 days' Pictures",
      month: "This Month's Pictures",
      custom: "Pictures"
    };
    return labels[this.activeDateTab] || "Pictures";
  }

  /** Count for date tab when no building selected (from API response, same as Hygiene Report) */
  getDateTabPicturesCount(): number {
    const d = this.reportData;
    if (!d) return 0;
    switch (this.activeDateTab) {
      case 'today': return d.todayPhotoCount ?? 0;
      case 'yesterday': return d.yesterdayPhotoCount ?? 0;
      case 'last7': return d.last7DaysPhotoCount ?? 0;
      case 'month': return d.thisMonthPhotoCount ?? 0;
      default: return 0;
    }
  }
}
